import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import './Marketplace.css';
import './CreateTask.css';

const DOMAINS = ['软件工程','人工智能','计算机学','电子信息','通信工程','大数据','理论研究','知识图谱','机器人','无人系统','创意征集','文献翻译','医学','政策法规','其他'];
const BUDGET_MODES = ['固定预算','按阶段报价','服务者报价'];
const PUBLISH_MODES = ['平台公开','定向邀请','沟通后确认'];

export default function CreateTask() {
  const { language, translatePhrase } = useLanguage();
  const text = language === 'zh' ? {
    kicker: '发布需求',
    title: '发布需求',
    subtitle: '把目标、预算、交付时间和验收标准说明清楚，方便服务者判断是否接单并给出方案。',
    basic: '需求资料',
    taskTitle: '需求标题',
    titlePlaceholder: '如：开发一个企业官网与后台数据看板',
    category: '需求分类',
    chooseCategory: '请选择领域',
    deadline: '期望完成时间',
    description: '交付说明',
    descriptionPlaceholder: '请说明需求背景、具体范围、交付物、验收标准和沟通偏好。\n\n建议包含：\n· 要解决的问题\n· 必须完成的功能或内容\n· 文件、源码或素材交付要求\n· 如何判断交付合格',
    tags: '技能标签（逗号分隔）',
    tagsPlaceholder: '如：React, Node.js, Logo 设计, 自动化脚本',
    reward: '预算与交付',
    rewardMode: '报价方式',
    publishMode: '接单方式',
    budgetMin: '预算下限（元）',
    budgetMax: '预算上限（元）',
    posting: '发布中...',
    submit: '发布需求',
    cancel: '取消',
    requiredError: '请填写需求标题、交付说明和需求分类',
    failed: '发布失败',
    tipsTitle: '发布前确认',
    tips: ['需求会展示在需求大厅，服务者可提交方案或报价', '不要在需求里填写手机号、微信、邮箱等站外联系方式', '预算会影响服务者判断，请尽量给出真实区间', '发布后可在需求详情中查看方案、选择服务者并进入托管付款流程'],
    modeTitle: '报价方式',
    modeTips: [
      ['固定预算', '预算明确，适合范围清晰的小项目'],
      ['按阶段报价', '适合需要分阶段验收和付款的项目'],
      ['服务者报价', '先收集方案，再选择合适的报价'],
    ],
    steps: ['填写需求', '选择方案', '托管付款'],
  } : {
    kicker: 'Post requirement',
    title: 'Post requirement',
    subtitle: 'Describe the goal, budget, timeline, and acceptance criteria so providers can quote with enough context.',
    basic: 'Requirement details',
    taskTitle: 'Requirement title',
    titlePlaceholder: 'Example: build a company website and admin dashboard',
    category: 'Requirement category',
    chooseCategory: 'Select a domain',
    deadline: 'Expected delivery date',
    description: 'Delivery brief',
    descriptionPlaceholder: 'Describe the background, scope, deliverables, acceptance criteria, and communication preference.\n\nRecommended:\n· Problem to solve\n· Required features or content\n· Files, source code, or assets to deliver\n· How acceptance will be judged',
    tags: 'Skill tags, separated by commas',
    tagsPlaceholder: 'Example: React, Node.js, logo design, automation scripts',
    reward: 'Budget and delivery',
    rewardMode: 'Quote method',
    publishMode: 'Provider access',
    budgetMin: 'Minimum budget',
    budgetMax: 'Maximum budget',
    posting: 'Posting...',
    submit: 'Post requirement',
    cancel: 'Cancel',
    requiredError: 'Please fill in requirement title, delivery brief, and category',
    failed: 'Publishing failed',
    tipsTitle: 'Before posting',
    tips: ['The requirement appears in the requirement hall for providers to submit proposals or quotes', 'Do not include phone, WeChat, email, or other off-platform contacts', 'Use a realistic budget range so providers can judge fit', 'After posting, select a provider from the requirement detail and continue to escrow payment'],
    modeTitle: 'Quote method',
    modeTips: [
      ['Fixed budget', 'Best when the scope is already clear'],
      ['Milestone quote', 'Best for staged delivery and acceptance'],
      ['Provider quote', 'Collect proposals first, then choose the best quote'],
    ],
    steps: ['Write brief', 'Choose proposal', 'Escrow payment'],
  };
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', budget_min: '', budget_max: '',
    category: '', tags: '', deadline: '',
    reward_mode: '固定预算', publish_mode: '平台公开',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title || !form.description || !form.category) {
      setError(text.requiredError);
      return;
    }
    setLoading(true);
    try {
      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const res = await api.post('/tasks', {
        ...form,
        budget_min: Number(form.budget_min) || 0,
        budget_max: Number(form.budget_max) || 0,
        tags,
      });
      navigate(`/tasks/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || text.failed);
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <div className="market-page post-flow-page">
      <div className="container publish-wrap">
        <header className="post-flow-hero">
          <div>
            <div className="market-kicker">{text.kicker}</div>
            <h1 className="detail-title">{text.title}</h1>
            <p>{text.subtitle}</p>
          </div>
          <div className="post-flow-steps" aria-label={text.title}>
            {text.steps.map((step, index) => (
              <span key={step}><b>{index + 1}</b>{step}</span>
            ))}
          </div>
        </header>

      <div className="publish-layout post-flow-layout">
        <div className="detail-card post-flow-card">
          <form onSubmit={handleSubmit}>
            <div className="post-flow-section-title">{text.basic}</div>

            <div className="form-group">
              <label>{text.taskTitle} <span className="required">*</span></label>
              <input value={form.title} onChange={set('title')}
                placeholder={text.titlePlaceholder} />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>{text.category} <span className="required">*</span></label>
                <select value={form.category} onChange={set('category')}>
                  <option value="">{text.chooseCategory}</option>
                  {DOMAINS.map(d => <option key={d} value={d}>{translatePhrase(d)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>{text.deadline}</label>
                <input type="date" value={form.deadline} onChange={set('deadline')} />
              </div>
            </div>

            <div className="form-group">
              <label>{text.description} <span className="required">*</span></label>
              <textarea
                value={form.description}
                onChange={set('description')}
                placeholder={text.descriptionPlaceholder}
                rows={8}
              />
            </div>

            <div className="form-group">
              <label>{text.tags}</label>
              <input value={form.tags} onChange={set('tags')}
                placeholder={text.tagsPlaceholder} />
            </div>

            <div className="post-flow-section-title">{text.reward}</div>

            <div className="grid-2">
              <div className="form-group">
                <label>{text.rewardMode}</label>
                <select value={form.reward_mode} onChange={set('reward_mode')}>
                  {BUDGET_MODES.map(m => <option key={m} value={m}>{translatePhrase(m)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>{text.publishMode}</label>
                <select value={form.publish_mode} onChange={set('publish_mode')}>
                  {PUBLISH_MODES.map(m => <option key={m} value={m}>{translatePhrase(m)}</option>)}
                </select>
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>{text.budgetMin}</label>
                <input type="number" value={form.budget_min} onChange={set('budget_min')}
                  placeholder="0" min="0" />
              </div>
              <div className="form-group">
                <label>{text.budgetMax} <span className="required">*</span></label>
                <input type="number" value={form.budget_max} onChange={set('budget_max')}
                  placeholder="5000" min="0" />
              </div>
            </div>

            {error && <p className="error-msg">{error}</p>}

            <div className="post-flow-actions">
              <button type="submit" className="btn btn-primary ct-submit-btn" disabled={loading}>
                {loading ? text.posting : text.submit}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>{text.cancel}</button>
            </div>
          </form>
        </div>

        <div className="ct-tips post-flow-side">
          <div className="detail-card ct-tip-card">
            <h4>{text.tipsTitle}</h4>
            <ul>
              {text.tips.map(item => <li key={item}>{item}</li>)}
            </ul>
          </div>
          <div className="detail-card ct-tip-card">
            <h4>{text.modeTitle}</h4>
            {text.modeTips.map(([title, desc]) => (
              <div className="ct-mode-item" key={title}>
                <strong>{title}</strong>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
